import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  FileImage, 
  Upload, 
  Trash2,
  FileText,
  CheckCircle,
  Loader2,
  Play
} from 'lucide-react';

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [templateName, setTemplateName] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    const allowedTypes = [
      'image/png', 'image/jpeg', 'image/jpg', 'image/webp', 
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];
    const allowedExtensions = ['png', 'jpg', 'jpeg', 'webp', 'pdf', 'doc', 'docx'];
    const fileExt = file.name.toLowerCase().split('.').pop();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExt)) {
      toast.error('Please upload a PNG, JPEG, WEBP, PDF, DOC, or DOCX file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/templates/upload?name=${encodeURIComponent(templateName)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Template uploaded and parsed successfully!');
      setTemplateName('');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await api.delete(`/templates/${templateId}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  }, [templateName]);

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Invoice Templates</h1>
          <p className="page-subtitle">Upload and manage your invoice templates</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={() => navigate('/redmine-tasks')}
            variant="outline"
            data-testid="redmine-tasks-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Redmine Tasks
          </Button>
          <Button 
            onClick={() => navigate('/pocketbook-invoice')}
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
            data-testid="pocketbook-invoice-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Pocketbook UA
          </Button>
          <Button 
            onClick={() => navigate('/pocketbook-wise')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="pocketbook-wise-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Pocketbook Wise
          </Button>
        </div>
      </div>

      {/* Upload Section */}
      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#4F46E5]" />
            Upload Template
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="form-group max-w-md">
            <Label htmlFor="templateName">Template Name *</Label>
            <Input
              id="templateName"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g., Standard Invoice Template"
              data-testid="template-name-input"
            />
          </div>

          <div
            className={`file-upload-zone ${dragActive ? 'dragging' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            data-testid="file-upload-zone"
          >
            <input
              type="file"
              id="file-upload"
              accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleInputChange}
              className="hidden"
              disabled={uploading}
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <div className="flex flex-col items-center gap-3">
                {uploading ? (
                  <Loader2 className="w-12 h-12 text-[#4F46E5] animate-spin" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#4F46E5]/10 flex items-center justify-center">
                    <FileImage className="w-8 h-8 text-[#4F46E5]" />
                  </div>
                )}
                <div>
                  <p className="text-lg font-medium text-slate-900">
                    {uploading ? 'Uploading & Parsing...' : 'Drop your invoice template here'}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    or click to browse (PNG, JPEG, WEBP, PDF, DOC, DOCX - max 10MB)
                  </p>
                </div>
              </div>
            </label>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">How it works:</h4>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Upload a sample invoice image or PDF</li>
              <li>Our AI (OpenAI Vision) will analyze and detect fields</li>
              <li>The system identifies placeholders like invoice number, dates, client info, and line items</li>
              <li>Use the parsed template structure when generating new invoices</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Templates List */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Your Templates</h2>
        
        {templates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template, index) => (
              <Card 
                key={template.id} 
                className="dashboard-card card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`template-card-${template.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-[#4F46E5]/10 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#4F46E5]" />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(template.id)}
                      className="h-8 w-8 p-0 hover:bg-rose-50"
                      data-testid={`delete-template-${template.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </div>
                  
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{template.name}</h3>
                  
                  <div className="space-y-2 mb-4">
                    <p className="text-sm text-slate-500">
                      Type: <span className="text-slate-700">{template.file_type?.split('/')[1]?.toUpperCase() || 'Unknown'}</span>
                    </p>
                    <p className="text-sm text-slate-500">
                      Created: <span className="text-slate-700">
                        {new Date(template.created_at).toLocaleDateString()}
                      </span>
                    </p>
                  </div>

                  {/* Detected Fields */}
                  {template.fields && template.fields.length > 0 && (
                    <div className="border-t border-slate-100 pt-4">
                      <p className="text-xs font-medium text-slate-500 uppercase mb-2">Detected Fields</p>
                      <div className="flex flex-wrap gap-1.5">
                        {template.fields.slice(0, 6).map((field, idx) => (
                          <span 
                            key={idx}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded"
                          >
                            <CheckCircle className="w-3 h-3 text-emerald-500" />
                            {field.name}
                          </span>
                        ))}
                        {template.fields.length > 6 && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">
                            +{template.fields.length - 6} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Use Template Button - only for DOCX */}
                  {(template.file_type?.includes('wordprocessingml') || 
                    template.file_type?.includes('msword') ||
                    template.file_type?.includes('docx')) && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <Button
                        onClick={() => navigate(`/templates/${template.id}/build`)}
                        className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                        data-testid={`use-template-${template.id}`}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Use This Template
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="dashboard-card">
            <CardContent className="py-12">
              <div className="empty-state">
                <div className="empty-state-icon">
                  <FileImage className="w-8 h-8 text-slate-400" />
                </div>
                <p className="empty-state-title">No templates yet</p>
                <p className="empty-state-text">
                  Upload your first invoice template to get started with AI-powered field detection
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
